/**
 * Taşınan yük değerlerini okunur hale getirir.
 *
 * Değerler kilogram cinsinden geliyor. Tek bir kaldırma birkaç bin kilogram
 * olabiliyor ama **kümülatif** toplamlar (bir göstergenin ömrü boyunca taşıdığı yük,
 * ya da bütün filonun toplamı) altı-yedi haneye çıkıyor — `261058.00` gibi bir sayı
 * ekranda okunmuyor.
 *
 * Bu yüzden: bin kilogramın üzerinde **ton**, bir milyonun üzerinde **bin ton**;
 * binlik ayracı Türkçe biçimde (`1.234.567`).
 *
 * ## Neden ortak dosyada
 *
 * Bu iki fonksiyon önce yalnızca `TotalWeightChart` içinde yerel olarak vardı
 * ("Son 6 Ay Taşınan Yük" düzeltmesiyle geldi). Pano kartı ve onarım modülündeki
 * gösterge kartları ise ham `toFixed(2)` gösteriyordu — yani **aynı ağırlık iki
 * ekranda farklı okunuyordu**. 2026-10-07'de ortak yardımcıya çıkarıldı.
 *
 * Yeni bir yerde ağırlık gösterilecekse buradan kullanılmalı; ikinci bir biçim
 * uydurulmamalı.
 */

/**
 * Uzun biçim: `"261 ton (261.058 kg)"`.
 *
 * Parantezli kısım kesin değeri koruyor — ton'a yuvarlama bilgiyi gizlemesin.
 * Kart ve ipucu (tooltip) gibi yer sıkıntısı olmayan alanlar için.
 *
 * @param val kilogram; `null`/`undefined`/`NaN` için `"-"`
 */
export function formatWeight(val: number | string | null | undefined): string {
  const num = toNumber(val)
  if (num === null) return '-'
  if (num >= 1000) {
    const ton = num / 1000
    // 1000 tonun altinda tek ondalik yeterli, ustunde ondalik gereksiz ayrinti.
    const text =
      ton >= 1000
        ? Math.round(ton).toLocaleString('tr-TR')
        : ton.toLocaleString('tr-TR', {maximumFractionDigits: 1})
    return `${text} ton (${num.toLocaleString('tr-TR')} kg)`
  }
  return `${num.toLocaleString('tr-TR')} kg`
}

/**
 * Kısa biçim: `"261 t"`. Parantezli ayrıntı yok.
 *
 * Grafik ekseni gibi dar alanlar için. Kesin değer gerekiyorsa
 * {@link formatWeight} kullanılmalı.
 *
 * @param val kilogram; `null`/`undefined`/`NaN` için boş metin
 */
export function formatWeightShort(val: number | string | null | undefined): string {
  const num = toNumber(val)
  if (num === null) return ''
  if (num >= 1000) {
    const ton = num / 1000
    return ton >= 1000
      ? `${Math.round(ton).toLocaleString('tr-TR')} t`
      : `${ton.toLocaleString('tr-TR', {maximumFractionDigits: 1})} t`
  }
  return `${num.toLocaleString('tr-TR')} kg`
}

/**
 * Sayıya çevirir; çevrilemiyorsa `null`.
 *
 * Metin de kabul ediyor çünkü bazı uçlar sayıyı string olarak döndürüyor (P2-8'de
 * büyük kısmı Double'a çevrildi ama hepsi değil). `parseFloat` yerine `Number`:
 * `parseFloat("12abc")` sessizce `12` döner, `Number("12abc")` ise `NaN` — bozuk
 * veriyi geçerli göstermek istemiyoruz.
 */
function toNumber(val: number | string | null | undefined): number | null {
  if (val === null || val === undefined || val === '') return null
  const num = typeof val === 'number' ? val : Number(val)
  return isNaN(num) ? null : num
}
